import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { IoLogoFacebook, IoLogoTwitter, IoLogoWhatsapp, IoLink, IoCalendarOutline, IoPersonOutline, IoTime, IoEye, IoArrowBack } from 'react-icons/io5';
import MetaTags from '../../components/layout/MetaTags';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import FadeInView from '../../components/FadeInView';
import { useBlogPost, useBlogPosts } from '../../hooks/useBlog';
import { CATEGORIES, getCategoryStyles } from '../../constants/categories';
import parse from 'html-react-parser';
import { BlogDetailSkeleton } from '../../components/SkeletonLoader';
import ImageGallery from '../../components/blog/ImageGallery';
import './BlogDetail.css';

// Calculate reading time
const calculateReadingTime = (content) => {
  if (!content) return 1;
  const wordsPerMinute = 200;
  const text = content.replace(/<[^>]*>/g, '');
  const wordCount = text.split(/\s+/).length;
  return Math.max(1, Math.ceil(wordCount / wordsPerMinute));
};

const BlogDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { post, loading, error } = useBlogPost(slug);
  const { posts: allPosts } = useBlogPosts();
  const [isCopied, setIsCopied] = useState(false);
  const [formattedImageUrl, setFormattedImageUrl] = useState('');
  const [scrollProgress, setScrollProgress] = useState(0);

  // Scroll progress handler
  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.scrollY / totalHeight) * 100;
      setScrollProgress(Math.min(100, Math.max(0, progress)));
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Get related posts (same category, excluding current post)
  const relatedPosts = allPosts
    .filter(p => p.slug !== slug && p.category === post?.category)
    .slice(0, 3);

  // Organize images by position
  const imagesByPosition = useMemo(() => {
    if (!post) return { top: [], middle: [], bottom: [] };
    
    const images = post.images && Array.isArray(post.images) ? post.images : [];
    
    if (images.length === 0 && post.image_url) {
      return {
        top: [{ url: post.image_url, alt: post.title, position: 'top' }],
        middle: [],
        bottom: []
      };
    }

    return {
      top: images.filter(img => img.position === 'top' || !img.position),
      middle: images.filter(img => img.position === 'middle'),
      bottom: images.filter(img => img.position === 'bottom')
    };
  }, [post]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const shareOnFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank');
  };

  const shareOnTwitter = () => {
    window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(post?.title || '')}`, '_blank');
  };

  const shareOnWhatsApp = () => {
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(`${post?.title || ''} ${window.location.href}`)}`, '_blank');
  };

  // Format image URL for OG tags
  useEffect(() => {
    if (post && post.image_url) {
      const baseUrl = window.location.origin;
      let imageUrl = post.image_url;
      
      if (post.image_url.startsWith('https://')) {
        imageUrl = post.image_url;
      } else if (post.image_url.startsWith('/storage/v1/object/public/')) {
        imageUrl = `https://eljlrbnnyfhlimkyqgmy.supabase.co${post.image_url}`;
      } else {
        imageUrl = `${baseUrl}${post.image_url.startsWith('/') ? '' : '/'}${post.image_url}`;
      }
      
      if (!imageUrl.startsWith('http')) {
        imageUrl = `${baseUrl}/${imageUrl.replace(/^\/+/, '')}`;
      }
      
      setFormattedImageUrl(imageUrl);
    } else {
      setFormattedImageUrl(`${window.location.origin}/images/default-thumbnail.jpg`);
    }
  }, [post]);

  useEffect(() => {
    if (post) {
      document.title = `${post.title} | YBUS`;
    }
  }, [post]);

  if (loading) {
    return (
      <div className="blog-detail-page">
        <Header />
        <main className="blog-detail-main">
          <div className="container-tight">
            <BlogDetailSkeleton />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="blog-detail-page">
        <Header />
        <main className="blog-detail-main">
          <div className="container-tight">
            <div className="blog-detail-error">
              <h1>Post tidak ditemukan</h1>
              <p>{error || 'Artikel blog yang Anda cari tidak tersedia.'}</p>
              <button className="blog-detail-back" onClick={() => navigate('/blog')}>
                <IoArrowBack /> Kembali ke Blog
              </button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="blog-detail-page">
      {/* Scroll Progress Bar */}
      <div className="blog-detail-progress-container">
        <div className="blog-detail-progress" style={{ width: `${scrollProgress}%` }} />
      </div>

      <Header />
      
      <main className="blog-detail-main">
        <MetaTags
          title={post.title}
          description={post.excerpt || `Baca artikel "${post.title}" di YBUS`}
          image={formattedImageUrl}
          url={`https://www.bahrululumic.com/blog/${post.slug}`}
          type="article"
          author={post.author || 'Admin YBUS'}
          publishDate={new Date(post.created_at).toISOString()}
        />
        
        <article className="blog-detail-article">
          {/* Header */}
          <FadeInView>
            <div className="blog-detail-header">
              <span 
                className="blog-detail-category"
                style={getCategoryStyles(post.category)}
              >
                {CATEGORIES[post.category.toUpperCase()]?.label}
              </span>
              <h1 className="blog-detail-title">{post.title}</h1>
              <div className="blog-detail-meta">
                <span className="blog-detail-meta-item">
                  <IoCalendarOutline />
                  {new Date(post.created_at).toLocaleDateString('id-ID', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
                <span className="blog-detail-meta-item">
                  <IoPersonOutline />
                  {post.author || 'Admin YBUS'}
                </span>
                <span className="blog-detail-meta-item">
                  <IoTime />
                  {calculateReadingTime(post.content)} menit baca
                </span>
                <span className="blog-detail-meta-item">
                  <IoEye />
                  {(post.view_count || 0).toLocaleString('id-ID')} views
                </span>
              </div>
            </div>
          </FadeInView>

          {/* Social Sharing */}
          <FadeInView delay={0.1}>
            <div className="blog-detail-share">
              <span className="blog-detail-share-label">Bagikan:</span>
              <button onClick={shareOnFacebook} className="blog-detail-share-btn" title="Share on Facebook">
                <IoLogoFacebook />
              </button>
              <button onClick={shareOnTwitter} className="blog-detail-share-btn" title="Share on Twitter">
                <IoLogoTwitter />
              </button>
              <button onClick={shareOnWhatsApp} className="blog-detail-share-btn" title="Share on WhatsApp">
                <IoLogoWhatsapp />
              </button>
              <button onClick={handleCopyLink} className="blog-detail-share-btn" title="Copy link">
                <IoLink />
                {isCopied && <span className="blog-detail-copied">Tersalin!</span>}
              </button>
            </div>
          </FadeInView>

          {/* Top Images */}
          {imagesByPosition.top.length > 0 && (
            <FadeInView delay={0.15}>
              <div className="blog-detail-images blog-detail-images-top">
                <ImageGallery images={imagesByPosition.top} />
              </div>
            </FadeInView>
          )}

          {/* Content — no FadeInView: tall articles never hit intersection threshold on mobile */}
          <div className="blog-detail-content">
            {post.content && typeof post.content === 'string' && (
              <>
                {imagesByPosition.middle.length > 0 ? (
                  <>
                    <div className="blog-detail-content-section">
                      {parse(post.content.substring(0, Math.floor(post.content.length / 2)))}
                    </div>
                    
                    <div className="blog-detail-images blog-detail-images-middle">
                      <ImageGallery images={imagesByPosition.middle} />
                    </div>
                    
                    <div className="blog-detail-content-section">
                      {parse(post.content.substring(Math.floor(post.content.length / 2)))}
                    </div>
                  </>
                ) : (
                  parse(post.content)
                )}
              </>
            )}
            {(!post.content || typeof post.content !== 'string') && (
              <p>Tidak ada konten tersedia untuk artikel ini.</p>
            )}
          </div>

          {/* Bottom Images */}
          {imagesByPosition.bottom.length > 0 && (
            <FadeInView>
              <div className="blog-detail-images blog-detail-images-bottom">
                <ImageGallery images={imagesByPosition.bottom} />
              </div>
            </FadeInView>
          )}

          {/* Back Button */}
          <FadeInView>
            <div className="blog-detail-actions">
              <button className="blog-detail-back" onClick={() => navigate('/blog')}>
                <IoArrowBack /> Kembali ke Blog
              </button>
            </div>
          </FadeInView>

          {/* Related Posts */}
          {relatedPosts.length > 0 && (
            <FadeInView>
              <div className="blog-detail-related">
                <h3 className="blog-detail-related-title">Artikel Terkait</h3>
                <div className="blog-detail-related-grid">
                  {relatedPosts.map(relatedPost => (
                    <Link 
                      key={relatedPost.id} 
                      to={`/blog/${relatedPost.slug}`}
                      className="blog-detail-related-card"
                    >
                      <div className="blog-detail-related-image">
                        <img 
                          src={relatedPost.image_url || 'https://via.placeholder.com/400x200?text=Bahrul+Ulum'} 
                          alt={relatedPost.title}
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/400x200?text=Bahrul+Ulum';
                          }}
                        />
                      </div>
                      <div className="blog-detail-related-content">
                        <h4>{relatedPost.title}</h4>
                        <span>
                          {new Date(relatedPost.created_at).toLocaleDateString('id-ID', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </FadeInView>
          )}
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default BlogDetail;
